import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * `<stock-pill>` — keeps the PDP inventory / lead-time pill in sync with the
 * selected variant.
 *
 * The pill's label and state class are computed server-side because it reads a
 * per-variant metafield (`custom.leadtime_variant`) that is NOT present in the
 * client-side variant JSON. So on a variant change we take the freshly rendered
 * pill out of the section HTML the variant-picker already fetched and morph this
 * element to match — the same approach as `assets/product-inventory.js`. No full
 * page reload.
 */
const STATE_CLASSES = ['stock-pill--in-stock', 'stock-pill--lead-time', 'stock-pill--out-of-stock'];

class StockPill extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updatePill);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updatePill);
  }

  /**
   * Updates the pill to reflect the newly selected variant.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePill = (event) => {
    // Combined listing: a different product was loaded, so adopt its id to keep
    // matching. Otherwise ignore updates dispatched for a different product.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPill = event.detail.data.html.querySelector('stock-pill');

    if (!newPill) return;

    // Swap only the state modifier (in-stock / lead-time / out-of-stock) so any
    // layout modifier already on this element (e.g. stock-pill--block) is kept.
    // morph({ childrenOnly }) updates the label text but never the host's class.
    const newState = STATE_CLASSES.find((stateClass) => newPill.classList.contains(stateClass));
    this.classList.remove(...STATE_CLASSES);
    if (newState) this.classList.add(newState);

    morph(this, newPill, { childrenOnly: true });
  };
}

if (!customElements.get('stock-pill')) {
  customElements.define('stock-pill', StockPill);
}
